













































































































































































import { Component, Vue } from "vue-property-decorator";
import MissionAnalyzerService from "@/classes/MissionAnalyzerService";

interface FactionCounts {
  west: number;
  east: number;
  independent: number;
  civilian: number;
  empty: number;
  dynamicSimEnabled: number;
}

interface MissionAnalysis {
  totalEntities: number;
  units: FactionCounts;
  logicCount: number;
  headlessClientCount: number;
  gameMasterModuleCount: number;
  otherObjectCount: number;
  simpleObjectCount: number;
  disabledSimulationCount: number;
  cbaSettings: string[];
}

@Component({
  metaInfo: {
    title: "Mission Analyzer – Newfound Pioneer Corps Arma 3",
    meta: [
      { vmid: "description", name: "description", content: "Upload and analyze Arma mission PBO files to extract and debinarize mission content." },
      { vmid: "og:title", property: "og:title", content: "Mission Analyzer – Newfound Pioneer Corps Arma 3" },
      { vmid: "og:description", property: "og:description", content: "Upload and analyze Arma mission PBO files to extract and debinarize mission content." },
      { vmid: "og:url", property: "og:url", content: "https://arma.npcs.games/mission-analyzer" },
    ],
  },
})
export default class MissionAnalyzer extends Vue {
  public missionFile: File | null = null;
  public analyzing: boolean = false;
  public analysis: MissionAnalysis | null = null;

  public async analyzeMission() {
    if (this.missionFile === null) {
      return;
    }

    this.analyzing = true;
    this.analysis = null;

    try {
      const response = await MissionAnalyzerService.uploadMission(this.missionFile);
      // Analyze the mission content
      this.analysis = this.performAnalysis(response);
    } catch (error: any) {
      let errorMessage = "Failed to analyze mission file. Please try again.";
      // Check for specific HTTP status codes
      if (error.response) {
        const statusCode = error.response.status;
        if (statusCode === 413) {
          errorMessage = "Mission file is too large. The API has a file size limit. Please try a smaller mission file.";
        } else if (statusCode === 415) {
          errorMessage = "Unsupported file type. Please ensure you're uploading a valid .pbo file.";
        } else if (statusCode >= 500) {
          errorMessage = "Server error occurred. Please try again later.";
        }
      } else if (error.message && error.message.includes("Network Error")) {
        errorMessage = "Network error. Please check your connection or try again later.";
      }
      this.$store.dispatch("showErrorSnackbar", errorMessage);
    } finally {
      this.analyzing = false;
    }
  }

  private performAnalysis(content: string): MissionAnalysis {
    // Total entities
    const allDataTypeMatches = content.match(/dataType="/g);
    const totalEntities = allDataTypeMatches ? allDataTypeMatches.length : 0;

    // Count logic entities
    const logicMatches = content.match(/dataType="Logic"/g);
    const logicCount = logicMatches ? logicMatches.length : 0;

    // Count headless clients
    const headlessClientMatches = content.match(/type="HeadlessClient_F"/g);
    const headlessClientCount = headlessClientMatches ? headlessClientMatches.length : 0;

    // Count Game Master modules (Zeus)
    const gameMasterMatches = content.match(/type="ModuleCurator_F"/g);
    const gameMasterModuleCount = gameMasterMatches ? gameMasterMatches.length : 0;

    // Count objects with disabled simulation
    const disabledSimulationMatches = content.match(/disableSimulation=1/g);
    const disabledSimulationCount = disabledSimulationMatches ? disabledSimulationMatches.length : 0;

    // Count simple objects
    const simpleObjectMatches = content.match(/createAsSimpleObject=1/g);
    const simpleObjectCount = simpleObjectMatches ? simpleObjectMatches.length : 0;

    // Extract CBA settings
    const cbaSettings = this.extractCBASettings(content);

    // Analyze objects and categorize them
    const { units, otherObjectCount } = this.analyzeObjects(content);

    return {
      totalEntities,
      units,
      logicCount,
      headlessClientCount,
      gameMasterModuleCount,
      otherObjectCount,
      disabledSimulationCount,
      simpleObjectCount,
      cbaSettings,
    };
  }

  private extractAllItemBlocks(content: string): string[] {
    const blocks: string[] = [];
    const itemPattern = /class\s+Item\d+\s*[\r\n\s]*\{/g;
    let match = itemPattern.exec(content);
    while (match !== null) {
      const openBrace = match.index + match[0].lastIndexOf("{");
      let depth = 0;
      let i = openBrace;

      while (i < content.length) {
        if (content[i] === "{") {
          depth++;
        } else if (content[i] === "}") {
          depth--;
          if (depth === 0) {
            blocks.push(content.substring(match.index, i + 1));
            break;
          }
        }
        i++;
      }
      match = itemPattern.exec(content);
    }

    return blocks;
  }

  private analyzeObjects(content: string): {
    units: FactionCounts;
    otherObjectCount: number;
  } {
    const units: FactionCounts = {
      west: 0,
      east: 0,
      independent: 0,
      civilian: 0,
      empty: 0,
      dynamicSimEnabled: 0,
    };

    let otherObjectCount = 0;

    // Use brace-balanced extraction so nested classes (PositionInfo, Attributes, etc.)
    // don't truncate the block before side= is reached.
    const blocks = this.extractAllItemBlocks(content);

    for (const block of blocks) {
      // Only process entity objects
      if (!block.includes("dataType=\"Object\"")) {
        continue;
      }

      // Extract side/faction from this block
      const sideMatch = block.match(/\bside="([^"]+)"/);
      const side = sideMatch ? sideMatch[1].toLowerCase() : "empty";

      // Check if dynamic simulation is explicitly enabled
      const hasDynamicSim = block.includes("dynamicSimulation=1");

      // Categorize based on side: any valid side means it's a unit
      if (side !== "empty") {
        this.incrementFactionCount(units, side);
        if (hasDynamicSim) {
          units.dynamicSimEnabled++;
        }
      } else {
        // side="Empty" means it's an object (building, prop, etc.)
        otherObjectCount++;
      }
    }

    return { units, otherObjectCount };
  }

  private incrementFactionCount(counts: FactionCounts, side: string) {
    switch (side) {
      case "west":
        counts.west++;
        break;
      case "east":
        counts.east++;
        break;
      case "independent":
      case "guer":
        counts.independent++;
        break;
      case "civilian":
      case "civ":
        counts.civilian++;
        break;
      default:
        counts.empty++;
    }
  }

  private getTotalCount(counts: FactionCounts): number {
    return counts.west + counts.east + counts.independent + counts.civilian + counts.empty;
  }

  private getActiveUnitsCount(): number {
    if (!this.analysis) { return 0; }
    const totalUnits = this.getTotalCount(this.analysis.units);
    const dynamicSimUnits = this.analysis.units.dynamicSimEnabled;
    return totalUnits - dynamicSimUnits;
  }

  private getFullySimulatedObjectsCount(): number {
    if (!this.analysis) { return 0; }
    return this.analysis.otherObjectCount - this.analysis.simpleObjectCount - this.analysis.disabledSimulationCount;
  }

  private extractCBASettings(content: string): string[] {
    const settings: string[] = [];
    // Check if CBA settings hash exists
    const cbaIndex = content.indexOf("property=\"cba_settings_hash\"");
    if (cbaIndex === -1) {
      return settings;
    }

    // Get a section after cba_settings_hash
    const searchSection = content.substring(cbaIndex, cbaIndex + 200000);
    // Find "class Item1" which contains the actual settings list
    // (Item0 is the hash, Item1 is the settings array)
    const item1Index = searchSection.indexOf("class Item1");
    if (item1Index === -1) {
      return settings;
    }
    // Get the Item1 section
    const item1Section = searchSection.substring(item1Index, item1Index + 150000);
    // Extract all value="..." that follow singleType="STRING" in this section
    // The format is:
    // singleType="STRING";
    // value="setting_name";
    const pattern = /singleType="STRING";[^}]*?value="([^"]+)"/g;
    let match = pattern.exec(item1Section);
    while (match !== null) {
      const value = match[1];
      // Skip the hash marker (just in case)
      if (value !== "#CBA_HASH#") {
        settings.push(value);
      }
      match = pattern.exec(item1Section);
    }

    return settings;
  }
}
